import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { BranchAdminService } from '../../../services/branch-admin.service';
import { AdminFamily, DetachResult, FamilyMember, StructureEvent } from '../../../models/branch-family.model';

/**
 * Plus.Admin — business family (+Business.AppWrapper/specs/branches SCR-B9, FR-1.5, FR-2.3, B2, B3,
 * B7). Shown on every business page: the family tree (head office, branches with label · code ·
 * catalogue mode), owner, plan and the ONE business count, the structure history, and — while the
 * page is in edit mode — "Detach branch…" with a required reason. Support can't link businesses.
 */
@Component({
  selector: 'app-business-family-panel',
  standalone: true,
  imports: [FormsModule, RouterLink, DatePipe],
  templateUrl: './business-family-panel.component.html',
  styleUrls: ['./business-family-panel.component.css']
})
export class BusinessFamilyPanelComponent implements OnChanges {
  @Input() businessId?: string;

  /** The page's edit mode: detaching is a write, so it is offered only with the edit lease. */
  @Input() editing = false;

  family: AdminFamily | null = null;
  history: StructureEvent[] = [];
  loading = false;
  loadError = '';

  /** The branch being detached (the inline confirm form is open). */
  detachTarget: FamilyMember | null = null;
  detachReason = '';
  detaching = false;
  detachError = '';
  detachResult: DetachResult | null = null;
  detachedName = '';

  constructor(private branches: BranchAdminService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessId']) {
      this.cancelDetach();
      this.detachResult = null;
      this.load();
    }
    if (changes['editing'] && !this.editing) {
      this.cancelDetach();
    }
  }

  get isFamily(): boolean {
    return !!this.family && this.family.branches.length > 0;
  }

  /** standalone | head office | branch of X — the business this page is about (B2). */
  get statusLine(): string {
    const f = this.family;
    if (!f) return '';
    if (!f.branches.length) return 'Standalone business — not part of a family.';
    const me = this.requested;
    if (me && me.structureRole === 'branch') {
      return `Branch of ${f.headOffice.name}${me.label ? ' · ' + me.label : ''}`;
    }
    return `Head office of ${f.branches.length} branch${f.branches.length === 1 ? '' : 'es'}`;
  }

  get requested(): FamilyMember | null {
    const f = this.family;
    if (!f) return null;
    const id = (f.requestedBusinessId || this.businessId || '').toLowerCase();
    return [f.headOffice, ...f.branches].find(m => m.businessId.toLowerCase() === id) ?? null;
  }

  load(): void {
    const id = this.businessId?.trim();
    this.family = null;
    this.history = [];
    this.loadError = '';
    if (!id) return;

    this.loading = true;
    forkJoin({ family: this.branches.getFamily(id), history: this.branches.getHistory(id) }).subscribe({
      next: ({ family, history }) => {
        this.family = family;
        this.history = history ?? [];
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.loadError = err.status === 404 ? 'This business was not found.' : 'The business family could not be loaded.';
      }
    });
  }

  openDetach(branch: FamilyMember): void {
    if (!this.editing || branch.structureRole !== 'branch') return;
    this.detachTarget = branch;
    this.detachReason = '';
    this.detachError = '';
    this.detachResult = null;
  }

  cancelDetach(): void {
    this.detachTarget = null;
    this.detachReason = '';
    this.detachError = '';
    this.detaching = false;
  }

  get canConfirmDetach(): boolean {
    return !!this.detachTarget && this.detachReason.trim().length > 0 && !this.detaching && this.editing;
  }

  confirmDetach(): void {
    const target = this.detachTarget;
    if (!target || !this.canConfirmDetach) return;

    this.detaching = true;
    this.detachError = '';
    this.branches.detach(target.businessId, this.detachReason.trim()).subscribe({
      next: result => {
        this.detaching = false;
        if (!result?.success) {
          this.detachError = result?.error || 'The branch could not be detached.';
          return;
        }
        this.detachResult = result;
        this.detachedName = target.label || target.name;
        this.detachTarget = null;
        this.detachReason = '';
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.detaching = false;
        this.detachError = err.error?.error || err.error?.message || 'The branch could not be detached.';
      }
    });
  }

  catalogueLabel(member: FamilyMember): string {
    return member.catalogueMode === 'own' ? 'own catalogue' : 'shared catalogue';
  }

  /** One line per structure event, in the console's plain voice. */
  describe(event: StructureEvent): string {
    const who = event.businessName || event.businessId;
    switch (event.eventType) {
      case 'created_as_branch': return `${who} created as branch`;
      case 'became_head_office': return `${who} became head office`;
      case 'linked': return `${who} linked as branch`;
      case 'detached':
        return event.fromStructureRole === 1 ? `${who} became standalone (last branch left)` : `${who} detached`;
      case 'closed': return `${who} closed`;
      case 'policy_changed': return `${who} branch rules changed`;
      case 'owner_changed': return `${who} owner changed with the family`;
      case 'catalogue_copied': {
        const counts = this.copyCounts(event.detailJson);
        return counts ? `${who} received its own catalogue (${counts})` : `${who} received its own catalogue`;
      }
      default: return `${who} ${event.eventType.replace(/_/g, ' ')}`;
    }
  }

  private copyCounts(json: string | null): string {
    if (!json) return '';
    try {
      const d = JSON.parse(json) as { items?: number; variants?: number };
      const parts: string[] = [];
      if (typeof d.items === 'number') parts.push(`${d.items} product${d.items === 1 ? '' : 's'}`);
      if (typeof d.variants === 'number' && d.variants > 0) parts.push(`${d.variants} variant${d.variants === 1 ? '' : 's'}`);
      return parts.join(', ');
    } catch {
      return '';
    }
  }
}
